import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "../../../component/layout/Layout";
import MomentHeaderCard from "../components/MomentHeaderCard";
import MomentPhonePreview from "../components/MomentPhonePreview";
import MomentTestSendModal from "../components/MomentTestSendModal";
import {
  getMyAccountStatus,
  getMessageAdDetail,
  getTargetingPrice,
  getCurrentUserInfo,
  sendTestMessage,
  createSendingReservation,
  updateSendingReservation,
  getCurrentPointInfo,
  type MomentAccountStatus,
  type CurrentUserInfo,
  type MomentAdDetailDto,
  type TargetingPriceDto,
  DEFAULT_TARGETING,
} from "../api/momentApi";
import { buildPreviewProps } from "../utils/messageParsers";
import { MESSAGE_TYPE_LABEL, KAKAO_DELAY_NOTICE_URL } from "../data/momentConstants";
import {
  buildSendTimeOptions,
  getLocalDateString,
  getLocalTimeString,
  getMinReservationDateTime,
  parseReservationDateTime,
  canEditReadyMessage,
  isBeforeMinReservationTime,
  getAvailableSendTimeOptions,
  isValidReservationTimeOption,
} from "../utils/dateTimeUtils";
import { formatPhoneNumber, isValidMobilePhoneNumber, normalizePhoneNumber } from "../utils/phoneUtils";

// 발송 시간 선택 옵션을 모듈 로드 시 한 번만 생성합니다.
const SEND_TIME_OPTIONS = buildSendTimeOptions();
const TEST_SEND_COOLDOWN_MS = 30_000;

// ─────────────────────────────────────────────
// 페이지 진입점
// ─────────────────────────────────────────────

export default function MomentReservePage() {
  return (
    <Layout>
      <MomentReserveGuard />
    </Layout>
  );
}

function MomentReserveGuard() {
  const navigate = useNavigate();
  const [account, setAccount] = useState<MomentAccountStatus | null | undefined>(undefined);

  useEffect(() => {
    getMyAccountStatus()
      .then((data) => setAccount(data ?? null))
      .catch(() => setAccount(null));
  }, []);

  if (account === undefined) {
    return (
      <div className="moment-page tw-min-h-[calc(100vh-200px)] tw-flex tw-items-center tw-justify-center tw-text-gray-400 tw-text-sm">
        불러오는 중...
      </div>
    );
  }

  const isReady = account !== null && account.status === "ACTIVE" && !!account.kakaoAdAccountId;

  if (!isReady) {
    return (
      <div className="moment-page tw-min-h-[calc(100vh-200px)] tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-4">
        <div className="tw-text-center tw-space-y-2">
          <p className="tw-text-lg tw-font-semibold tw-text-gray-700">채널 연동이 완료되지 않았습니다.</p>
          <p className="tw-text-sm tw-text-gray-500">
            메시지 광고를 예약하려면 광고계정과 카카오톡 채널 연동이 완료되어야 합니다.
          </p>
        </div>
        <button
          onClick={() => navigate("/moment")}
          className="tw-px-5 tw-py-2.5 tw-bg-[#5D87FF] tw-text-white tw-rounded-xl tw-text-sm tw-font-bold"
        >
          서비스 신청 페이지로 이동
        </button>
      </div>
    );
  }

  return <MomentReserveContent />;
}

// ─────────────────────────────────────────────
// 예약 콘텐츠
// ─────────────────────────────────────────────

function MomentReserveContent() {
  const navigate = useNavigate();
  const { kakaoAdId } = useParams<{ kakaoAdId: string }>();

  const [ad, setAd] = useState<MomentAdDetailDto | null>(null);
  const [adLoading, setAdLoading] = useState(true);

  const today = new Date();
  const minReservationDate = getMinReservationDateTime();
  const maxDay = new Date(today);
  maxDay.setDate(maxDay.getDate() + 14);
  const minDateStr = getLocalDateString(today);
  const maxDateStr = getLocalDateString(maxDay);

  const [sendDate, setSendDate] = useState(getLocalDateString(minReservationDate));
  const [sendTime, setSendTime] = useState(getLocalTimeString(minReservationDate));
  const [targetingResult, setTargetingResult] = useState<TargetingPriceDto | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [testPhone, setTestPhone] = useState("");
  const [statusMessage, setStatusMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [currentUser, setCurrentUser] = useState<CurrentUserInfo | null>(null);
  const [testCooldownSeconds, setTestCooldownSeconds] = useState(0);
  const currentUserPhone = currentUser?.phone ?? "";

  const getCooldownStorageKey = () =>
    `moment:test-send:cooldown:reserve:${kakaoAdId ?? "unknown"}`;

  const syncCooldownFromStorage = () => {
    const raw = window.sessionStorage.getItem(getCooldownStorageKey());
    if (!raw) {
      setTestCooldownSeconds(0);
      return;
    }

    const expiresAt = Number(raw);
    if (!Number.isFinite(expiresAt)) {
      window.sessionStorage.removeItem(getCooldownStorageKey());
      setTestCooldownSeconds(0);
      return;
    }

    const remainingMs = expiresAt - Date.now();
    if (remainingMs <= 0) {
      window.sessionStorage.removeItem(getCooldownStorageKey());
      setTestCooldownSeconds(0);
      return;
    }

    setTestCooldownSeconds(Math.ceil(remainingMs / 1000));
  };

  const isPastSelectedReservationTime = isBeforeMinReservationTime(sendDate, sendTime);
  const availableSendTimeOptions = getAvailableSendTimeOptions(sendDate, minDateStr);
  const minAvailableSendTime = sendDate === minDateStr ? availableSendTimeOptions[0] ?? null : null;

  // 광고 정보 로드
  useEffect(() => {
    if (!kakaoAdId) return;
    setAdLoading(true);
    getMessageAdDetail(kakaoAdId)
      .then((data) => {
        setAd(data);
        if (data.status === "READY" && data.sendDate) {
          const parsedReservation = parseReservationDateTime(data.sendDate);
          if (parsedReservation) {
            setSendDate(parsedReservation.date);
            setSendTime(parsedReservation.time);
          }
        }
      })
      .catch(() => setAd(null))
      .finally(() => setAdLoading(false));
  }, [kakaoAdId]);

  const isEditMode = ad?.status === "READY";
  const canEditScheduledMessage = ad?.status === "READY" ? canEditReadyMessage(ad.sendDate) : false;

  // 토스트
  useEffect(() => {
    if (!statusMessage) { setShowToast(false); return; }
    setShowToast(true);
    const hideTimer = window.setTimeout(() => setShowToast(false), 2400);
    const clearTimer = window.setTimeout(() => setStatusMessage(null), 2800);
    return () => { window.clearTimeout(hideTimer); window.clearTimeout(clearTimer); };
  }, [statusMessage]);

  useEffect(() => {
    if (!showTestModal) return;
    setTestPhone(formatPhoneNumber(currentUserPhone));
  }, [currentUserPhone, showTestModal]);

  useEffect(() => {
    getCurrentUserInfo()
      .then((data) => setCurrentUser(data))
      .catch(() => setCurrentUser(null));
  }, []);

  useEffect(() => {
    syncCooldownFromStorage();
    const timer = window.setInterval(syncCooldownFromStorage, 1000);
    return () => window.clearInterval(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kakaoAdId]);

  // DRAFT 상태 진입 시 모수 자동 조회
  useEffect(() => {
    if (!kakaoAdId || !ad || ad.status !== "DRAFT") return;
    getTargetingPrice(kakaoAdId, { ...DEFAULT_TARGETING, type: "WHOLE", deviceTypes: [] })
      .then((result) => setTargetingResult(result))
      .catch(() => {/* 자동 조회 실패는 무시 */});
  }, [kakaoAdId, ad]);

  // 날짜 변경 시 시간 보정
  useEffect(() => {
    if (!sendDate) return;
    if (availableSendTimeOptions.length === 0) return;
    if (!availableSendTimeOptions.includes(sendTime)) {
      setSendTime(availableSendTimeOptions[0]);
    }
  }, [availableSendTimeOptions, sendDate, sendTime]);

  const refreshPointViews = async () => {
    const pointInfo = await getCurrentPointInfo();
    const availablePoint = pointInfo.amount + pointInfo.freeAmount;
    window.dispatchEvent(new CustomEvent("bizchat:point-refresh"));
    setTargetingResult((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        cashAmount: pointInfo.amount,
        freeAmount: pointInfo.freeAmount,
        availablePoint,
        remainingPointAfterDeduction: availablePoint - prev.totalBudget,
      };
    });
  };

  // 테스트 발송
  const handleTestSend = async () => {
    if (!kakaoAdId) return;
    if (testCooldownSeconds > 0) {
      alert(`테스트 발송은 ${testCooldownSeconds}초 후 다시 시도할 수 있습니다.`);
      return;
    }
    const normalizedPhone = normalizePhoneNumber(testPhone);
    if (!normalizedPhone) { alert("테스트 발송 수신 번호를 입력해주세요."); return; }
    if (!isValidMobilePhoneNumber(normalizedPhone)) {
      alert("전화번호 형식이 올바르지 않습니다. 회원가입한 휴대전화 번호를 입력해주세요.");
      return;
    }
    setIsLoading(true);
    try {
      await sendTestMessage(kakaoAdId, normalizedPhone);
      await refreshPointViews();
      window.sessionStorage.setItem(getCooldownStorageKey(), String(Date.now() + TEST_SEND_COOLDOWN_MS));
      syncCooldownFromStorage();
      setShowTestModal(false);
      setTestPhone("");
      setStatusMessage({ type: "success", text: "테스트 발송이 완료되었습니다." });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "테스트 발송 중 오류가 발생했습니다.";
      setStatusMessage({ type: "error", text: msg });
      setShowTestModal(false);
    } finally {
      setIsLoading(false);
    }
  };

  // 발송 예약 / 예약 수정
  const handleReserve = async () => {
    if (!kakaoAdId) return;
    if (!sendDate || !sendTime) { alert("발송 날짜와 시간을 선택해주세요."); return; }
    if (!isValidReservationTimeOption(sendTime)) {
      alert("발송 예약 시간은 오전 8시부터 오후 8시 50분까지 5분 단위로만 선택할 수 있습니다.");
      return;
    }
    if (isEditMode && !canEditScheduledMessage) {
      alert("예약 수정은 발송 10분 전까지만 가능합니다.");
      navigate("/moment/list");
      return;
    }
    if (isPastSelectedReservationTime) {
      alert("발송 예약은 현재 시각 기준 10분 이후부터 가능합니다. 예약 시간을 다시 선택해주세요.");
      return;
    }
    setIsLoading(true);
    try {
      const sendDateTime = `${sendDate}T${sendTime}:00`;
      if (isEditMode) {
        // 모수 재조회 및 DB 갱신은 백엔드 서비스에서 처리 (포인트 재차감 없음)
        await updateSendingReservation(kakaoAdId, sendDateTime);
        setStatusMessage({ type: "success", text: "예약 일시가 수정되었습니다." });
      } else {
        // 모수 재조회 및 포인트 검증은 백엔드 서비스에서 처리
        // 포인트 부족 시 백엔드에서 에러를 반환하므로 catch 블록에서 처리
        await createSendingReservation(kakaoAdId, sendDateTime, "WHOLE");
        await refreshPointViews();
        setStatusMessage({ type: "success", text: "발송 예약이 완료되었습니다." });
      }
      setShowCompleteModal(true);
    } catch (e: unknown) {
      const msg =
        e instanceof Error
          ? e.message
          : isEditMode
          ? "예약 수정 중 오류가 발생했습니다."
          : "발송 예약 중 오류가 발생했습니다.";
      setStatusMessage({ type: "error", text: msg });
    } finally {
      setIsLoading(false);
    }
  };

  const estimatedCost = targetingResult?.totalBudget.toLocaleString() ?? null;
  const availablePoint = targetingResult?.availablePoint.toLocaleString() ?? null;
  const remainingPoint = targetingResult?.remainingPointAfterDeduction.toLocaleString() ?? null;

  const previewProps = ad ? buildPreviewProps(ad) : null;

  return (
    <div className="moment-page tw-px-6 tw-py-6">
      <div className="tw-max-w-[1100px] tw-mx-auto">
        <MomentHeaderCard
          title={isEditMode ? "예약 수정" : "발송 예약"}
          description={
            isEditMode
              ? "발송 예정 시간을 변경합니다."
              : "발송 날짜와 시간을 선택하고 모수를 확인한 후 예약합니다."
          }
        />

        {/* 토스트 */}
        {statusMessage && (
          <div
            className={`tw-fixed tw-top-24 tw-right-6 tw-z-[100] tw-w-[320px] tw-max-w-[calc(100vw-2rem)] tw-rounded-xl tw-border tw-px-4 tw-py-3 tw-shadow-lg tw-backdrop-blur-sm tw-transition-all tw-duration-300 ${
              statusMessage.type === "success"
                ? "tw-border-emerald-600 tw-bg-emerald-600 tw-text-white"
                : "tw-border-red-600 tw-bg-red-600 tw-text-white"
            } ${showToast ? "tw-translate-x-0 tw-opacity-100" : "tw-translate-x-6 tw-opacity-0"}`}
          >
            <div className="tw-text-sm tw-font-semibold">
              {statusMessage.type === "success" ? "처리 완료" : "처리 실패"}
            </div>
            <div className="tw-mt-1 tw-text-sm tw-whitespace-pre-line">{statusMessage.text}</div>
          </div>
        )}

        {/* 2컬럼 레이아웃 */}
        <div className="tw-mt-6 tw-flex tw-gap-6 tw-items-start">
          {/* 왼쪽: 폰 미리보기 */}
          <div className="tw-w-[360px] tw-flex-shrink-0 tw-sticky tw-top-6">
            {adLoading ? (
              <div className="tw-flex tw-items-center tw-justify-center tw-h-[600px] tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-shadow-sm tw-text-sm tw-text-gray-400">
                미리보기 로딩 중...
              </div>
            ) : previewProps ? (
              <MomentPhonePreview {...previewProps} />
            ) : (
              <div className="tw-flex tw-items-center tw-justify-center tw-h-[600px] tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-shadow-sm tw-text-sm tw-text-gray-400">
                광고 정보를 불러올 수 없습니다.
              </div>
            )}
          </div>

          {/* 오른쪽: 예약 폼 */}
          <div className="tw-flex-1 tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-shadow-sm tw-p-6 tw-space-y-6">
            {/* 광고 정보 */}
            {adLoading ? (
              <div className="tw-text-sm tw-text-gray-400">광고 정보를 불러오는 중...</div>
            ) : ad ? (
              <div className="tw-bg-gray-50 tw-rounded-xl tw-border tw-border-gray-200 tw-p-4">
                <div className="tw-flex tw-items-center tw-gap-3">
                  <div className="tw-flex-1">
                    <div className="tw-font-semibold tw-text-gray-800">{ad.adName}</div>
                    <div className="tw-text-xs tw-text-gray-500 tw-mt-0.5">
                      {MESSAGE_TYPE_LABEL[ad.messageType] ?? ad.messageType}
                    </div>
                  </div>
                  <span
                    className={`tw-text-xs tw-px-2 tw-py-1 tw-rounded-full tw-font-medium ${
                      isEditMode
                        ? "tw-bg-amber-100 tw-text-amber-700"
                        : "tw-bg-gray-100 tw-text-gray-600"
                    }`}
                  >
                    {isEditMode ? "발송대기" : "임시저장"}
                  </span>
                </div>
              </div>
            ) : (
              <div className="tw-text-sm tw-text-red-500">광고 정보를 불러올 수 없습니다.</div>
            )}

            {/* 발송 지연 안내 */}
            <div className="tw-bg-amber-50 tw-border tw-border-amber-200 tw-rounded-xl tw-px-4 tw-py-3">
              <div className="tw-text-xs tw-font-semibold tw-text-amber-700 tw-mb-1">메시지 발송 지연 안내</div>
              <p className="tw-text-xs tw-text-amber-700 tw-leading-relaxed">
                설정한 발송 시간보다 실제 발송 시간이 지연될 수 있습니다. 발송이 집중되는 시간대,
                스마트메시지 최적화, 시작 일시 1시간 이내 설정 등 다양한 요인에 의해 지연이 발생할 수 있으며,
                이는 오류 현상이 아닙니다.
              </p>
              <a
                href={KAKAO_DELAY_NOTICE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="tw-inline-block tw-mt-1.5 tw-text-xs tw-text-amber-800 tw-font-semibold hover:tw-underline"
              >
                자세한 내용 보기 →
              </a>
            </div>

            {/* 발송 예약 시간 */}
            <div className="tw-space-y-3">
              <div className="tw-font-semibold tw-text-gray-800 tw-text-sm">발송 예약 설정</div>
              <div className="tw-flex tw-gap-3">
                <div className="tw-flex tw-flex-col tw-gap-1 tw-flex-1">
                  <label className="tw-text-xs tw-text-gray-500">발송 날짜</label>
                  <input
                    type="date"
                    value={sendDate}
                    min={minDateStr}
                    max={maxDateStr}
                    onChange={(e) => setSendDate(e.target.value)}
                    className="tw-border tw-border-gray-300 tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm"
                  />
                </div>
                <div className="tw-flex tw-flex-col tw-gap-1 tw-flex-1">
                  <label className="tw-text-xs tw-text-gray-500">발송 시간 (08:00~20:50)</label>
                  <select
                    value={sendTime}
                    onChange={(e) => setSendTime(e.target.value)}
                    className="tw-border tw-border-gray-300 tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm"
                  >
                    {availableSendTimeOptions.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
              </div>
              {sendDate === minDateStr && isPastSelectedReservationTime && (
                <p className="tw-text-xs tw-text-red-600">
                  당일 발송은 현재 시각 기준 10분 이후부터 선택할 수 있습니다.
                </p>
              )}
              {sendDate === minDateStr && minAvailableSendTime && (
                <p className="tw-text-xs tw-text-gray-500">
                  오늘은 가장 빠른 예약 가능 시간이 {minAvailableSendTime}입니다.
                </p>
              )}
            </div>

            {/* 모수/단가 결과 */}
            {targetingResult && (
              <div className="tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-xl tw-p-4">
                <div className="tw-grid tw-grid-cols-2 tw-gap-3">
                  <div className="tw-rounded-xl tw-border tw-border-blue-100 tw-bg-white/85 tw-p-4">
                    <div className="tw-text-xs tw-text-gray-500">예상 발송 수</div>
                    <div className="tw-mt-2 tw-text-lg tw-font-bold tw-text-blue-700">
                      {targetingResult.contractCount.toLocaleString()}명
                    </div>
                  </div>
	                  <div className="tw-rounded-xl tw-border tw-border-blue-100 tw-bg-white/85 tw-p-4">
	                    <div className="tw-text-xs tw-text-gray-500">선차감 비용</div>
	                    <div className="tw-mt-2 tw-text-lg tw-font-bold tw-text-blue-700">{estimatedCost} P</div>
	                  </div>
                  <div className="tw-rounded-xl tw-border tw-border-blue-100 tw-bg-white/85 tw-p-4">
                    <div className="tw-text-xs tw-text-gray-500">총 사용 가능 포인트</div>
                    <div className="tw-mt-2 tw-text-lg tw-font-bold tw-text-gray-800">{availablePoint} P</div>
                  </div>
                  <div
                    className={`tw-rounded-xl tw-border tw-p-4 ${
                      targetingResult.remainingPointAfterDeduction >= 0
                        ? "tw-border-green-100 tw-bg-green-50/85"
                        : "tw-border-red-100 tw-bg-red-50/85"
                    }`}
	                  >
                    <div className="tw-text-xs tw-text-gray-500">차감 후 잔여 포인트</div>
                    <div
                      className={`tw-mt-2 tw-text-lg tw-font-bold ${
                        targetingResult.remainingPointAfterDeduction >= 0
                          ? "tw-text-green-700"
                          : "tw-text-red-700"
                      }`}
                    >
                      {remainingPoint} P
                    </div>
                  </div>
                </div>
                {targetingResult.remainingPointAfterDeduction < 0 && (
                  <p className="tw-mt-3 tw-text-xs tw-text-red-600">
                    예상 차감 포인트가 현재 보유 포인트를 초과합니다. 충전 후 진행해주세요.
                  </p>
                )}
	                <p className="tw-mt-5 tw-mb-0 tw-text-xs tw-leading-relaxed tw-text-red-500">
	                  * 현재 금액은 메시지 예약 시점 기준 선차감 비용입니다.
	                  <br />
	                  * 발송 5분 전 최종 모수 확인 결과에 따라 포인트가 추가 차감/복구될 수 있으며, 포인트가 부족할 시 발송이 취소 처리될 수 있습니다.
	                </p>
              </div>
            )}

            {/* 액션 버튼 */}
            <div className="tw-flex tw-justify-between tw-items-center tw-pt-4 tw-border-t tw-border-gray-100">
              <div className="tw-flex tw-gap-2">
                <button
                  type="button"
                  onClick={() => navigate("/moment/list")}
                  className="tw-px-4 tw-py-2 tw-bg-white hover:tw-bg-gray-50 tw-text-gray-500 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm tw-font-medium"
                >
                  목록으로
                </button>
                {isEditMode && canEditScheduledMessage && ad && (
                  <button
                    type="button"
                    onClick={() => navigate(`/moment/create?draftAdId=${kakaoAdId}`)}
                    className="tw-px-4 tw-py-2 tw-bg-white hover:tw-bg-gray-50 tw-text-indigo-600 tw-border tw-border-indigo-200 tw-rounded-lg tw-text-sm tw-font-medium"
                  >
                    메시지 수정
                  </button>
                )}
                {!isEditMode && ad && (
                  <button
                    type="button"
                    onClick={() => navigate(`/moment/create?draftAdId=${kakaoAdId}`)}
                    className="tw-px-4 tw-py-2 tw-bg-white hover:tw-bg-gray-50 tw-text-blue-600 tw-border tw-border-blue-200 tw-rounded-lg tw-text-sm tw-font-medium"
                  >
                    수정하기
                  </button>
                )}
              </div>
              <div className="tw-flex tw-gap-2">
                <button
                  type="button"
                  onClick={() => setShowTestModal(true)}
                  disabled={isLoading || !ad || testCooldownSeconds > 0}
                  className="tw-px-4 tw-py-2 tw-bg-white hover:tw-bg-gray-50 tw-text-gray-700 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm tw-font-semibold disabled:tw-opacity-50"
                >
                  {testCooldownSeconds > 0 ? `테스트 발송 (${testCooldownSeconds})` : "테스트 발송"}
                </button>
                <button
                  type="button"
                  onClick={handleReserve}
                  disabled={isLoading || !ad || (isEditMode && !canEditScheduledMessage) || (!isEditMode && targetingResult != null && targetingResult.remainingPointAfterDeduction < 0)}
                  className="tw-px-4 tw-py-2 tw-bg-[#5D87FF] hover:tw-bg-[#4570e6] tw-text-white tw-rounded-lg tw-text-sm tw-font-bold tw-border-0 disabled:tw-opacity-50"
                >
                  {isLoading ? "처리 중..." : isEditMode ? "예약 수정" : "발송 예약"}
                </button>
              </div>
            </div>
            {isEditMode && !canEditScheduledMessage && (
              <p className="tw-mt-3 tw-text-xs tw-text-amber-700">
                메시지 내용 수정은 발송 10분 전까지만 가능합니다.
              </p>
            )}
          </div>
        </div>
      </div>

      <MomentTestSendModal
        open={showTestModal}
        phone={testPhone}
        isLoading={isLoading}
        cooldownSeconds={testCooldownSeconds}
        onClose={() => { setShowTestModal(false); setTestPhone(""); }}
        onSubmit={handleTestSend}
        saveGuideText="마지막 저장 기준으로 메시지가 발송됩니다."
      />

      {/* 발송 예약 완료 모달 */}
      {showCompleteModal && (
        <div className="tw-fixed tw-inset-0 tw-bg-black/50 tw-flex tw-items-center tw-justify-center tw-z-50">
          <div className="tw-bg-white tw-rounded-2xl tw-p-6 tw-w-full tw-max-w-sm tw-shadow-xl">
            <h3 className="tw-font-bold tw-text-lg tw-mb-4">
              {isEditMode ? "예약 수정 완료" : "발송 예약 완료"}
            </h3>
            <div className="tw-space-y-2 tw-text-sm tw-text-gray-700 tw-mb-4">
              {ad && (
                <div className="tw-flex tw-justify-between">
                  <span className="tw-text-gray-500">광고명</span>
                  <span className="tw-font-semibold">{ad.adName}</span>
                </div>
              )}
              <div className="tw-flex tw-justify-between">
                <span className="tw-text-gray-500">발송 시간</span>
                <span className="tw-font-semibold">{sendDate} {sendTime}</span>
              </div>
              {isEditMode && (
                <p className="tw-text-xs tw-text-blue-600">
                  * 발송 시간만 변경됩니다. 비용은 기존 예약 기준으로 유지됩니다.
                </p>
              )}
            </div>
            {!isEditMode && (
              <div className="tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-lg tw-px-3 tw-py-3 tw-mb-4">
                <ul className="tw-text-xs tw-text-red-500 tw-space-y-1.5 tw-list-disc tw-pl-4">
                  <li>메시지 등록 시점의 친구 수와 메시지 발송 시점의 친구 수가 다를 수 있어 발송 5분 전 최종 모수 확인 결과에 따라 포인트가 추가 차감될 수 있습니다.</li>
                  <li>최종 차감 시점에 포인트가 부족하면 발송이 취소될 수 있습니다.</li>
                  <li>안정적인 발송을 위해 예상 차감 금액보다 넉넉하게 포인트를 보유해주세요.</li>
                </ul>
              </div>
            )}
            {/* 발송 지연 안내 */}
            <div className="tw-bg-amber-50 tw-border tw-border-amber-200 tw-rounded-lg tw-px-3 tw-py-2.5 tw-mb-4">
              <p className="tw-text-xs tw-text-amber-700 tw-leading-relaxed">
                설정 시간보다 실제 발송이 지연될 수 있습니다.
                <br />
                이는 오류가 아닙니다.
              </p>
              <a
                href={KAKAO_DELAY_NOTICE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="tw-text-xs tw-text-amber-800 tw-font-semibold hover:tw-underline"
              >
                발송 지연 안내 자세히 보기 →
              </a>
            </div>
            <div className="tw-flex">
              <button
                onClick={() => {
                  setShowCompleteModal(false);
                  navigate("/moment/list");
                }}
                className="tw-w-full tw-px-4 tw-py-2 tw-bg-[#5D87FF] tw-text-white tw-rounded-lg tw-text-sm tw-font-bold disabled:tw-opacity-50 tw-border-0"
              >
                확인
              </button>
            </div>
          </div>
        </div>
        )}
    </div>
  );
}
